import React, { useState, useEffect, useRef } from "react";
import "./css/ContactSupport.css";
import { useTranslation } from "../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faComments,
  faBook,
  faHeadset,
  faClock,
  faPaperPlane,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { API_BASE_URL } from "../../config/apiConfig";

function ContactSupport() {
  const containerRef = useRef(null);
  const supportTicketRef = useRef(null);
  const { t } = useTranslation();

  // Scroll to support ticket section
  const scrollToSupportTicket = () => {
    if (supportTicketRef.current) {
      supportTicketRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    // Try multiple scroll methods to ensure it works
    window.scrollTo(0, 0);

    // Also scroll the container element itself
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "instant",
        block: "start",
      });
    }

    // Also try to scroll any parent hero container
    const heroElement = document.querySelector(".hero");
    if (heroElement) {
      heroElement.scrollTop = 0;
    }
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    priority: "medium",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation with sanitized inputs
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedSubject =
      formData.subject.trim() ||
      `Support Ticket - ${formData.priority} priority`;
    const trimmedMessage = formData.message.trim();

    if (!trimmedName) {
      setFormStatus({
        submitted: false,
        error: true,
        message: t("contactSupport.validation.nameRequired"),
      });
      return;
    }
    if (trimmedName.length > 100) {
      setFormStatus({
        submitted: false,
        error: true,
        message: t("contactSupport.validation.nameTooLong"),
      });
      return;
    }
    if (!trimmedEmail) {
      setFormStatus({
        submitted: false,
        error: true,
        message: t("contactSupport.validation.emailRequired"),
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setFormStatus({
        submitted: false,
        error: true,
        message: t("contactSupport.validation.emailInvalid"),
      });
      return;
    }
    if (!trimmedMessage) {
      setFormStatus({
        submitted: false,
        error: true,
        message: t("contactSupport.validation.messageRequired"),
      });
      return;
    }
    if (trimmedMessage.length > 5000) {
      setFormStatus({
        submitted: false,
        error: true,
        message: t("contactSupport.validation.messageTooLong"),
      });
      return;
    }

    // Include priority in the message body
    const messageWithPriority = `[Priority: ${formData.priority.toUpperCase()}]\n\n${trimmedMessage}`;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/base/contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: trimmedName,
            email: trimmedEmail,
            subject: trimmedSubject,
            message: messageWithPriority,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setFormStatus({
          submitted: true,
          error: false,
          message: data.message || t("contactSupport.success.message"),
        });

        // Reset form
        setFormData({
          name: "",
          email: "",
          subject: "",
          priority: "medium",
          message: "",
        });

        // Clear success message after 5 seconds
        setTimeout(() => {
          setFormStatus({ submitted: false, error: false, message: "" });
        }, 5000);
      } else {
        throw new Error(data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Support ticket submission error:", error);
      setFormStatus({
        submitted: false,
        error: true,
        message: error.message || t("contactSupport.error.submit"),
      });
    }
  };

  return (
    <div className="contact-support-container" ref={containerRef}>
      <div className="contact-support-header">
        <h1>
          <FontAwesomeIcon icon={faHeadset} className="header-icon" />
          {t("contactSupport.title")}
        </h1>
        <p className="subtitle">{t("contactSupport.subtitle")}</p>
      </div>

      <div className="contact-support-content">
        {/* Contact Methods */}
        <div className="contact-methods">
          <div className="contact-method-card">
            <FontAwesomeIcon icon={faEnvelope} className="method-icon" />
            <h3>{t("contactSupport.email.title")}</h3>
            <p className="method-info">{t("contactSupport.email.info")}</p>
            <p className="method-description">
              {t("contactSupport.email.description")}
            </p>
            <button
              className="contact-method-btn"
              onClick={scrollToSupportTicket}
            >
              {t("contactSupport.email.button")}
            </button>
          </div>

          <div className="contact-method-card">
            <FontAwesomeIcon icon={faPhone} className="method-icon" />
            <h3>{t("contactSupport.phone.title")}</h3>
            <p className="method-info">{t("contactSupport.phone.info")}</p>
            <p className="method-description">
              {t("contactSupport.phone.description")}
            </p>
            <a href="tel:1-301-237-7843" className="contact-method-btn">
              {t("contactSupport.phone.button")}
            </a>
          </div>

          <div className="contact-method-card">
            <FontAwesomeIcon icon={faComments} className="method-icon" />
            <h3>{t("contactSupport.chat.title")}</h3>
            <p className="method-info">{t("contactSupport.chat.info")}</p>
            <p className="method-description">
              {t("contactSupport.chat.description")}
            </p>
            <button className="contact-method-btn" disabled>
              {t("common.comingSoon")}
            </button>
          </div>

          <div className="contact-method-card">
            <FontAwesomeIcon icon={faBook} className="method-icon" />
            <h3>{t("contactSupport.helpCenter.title")}</h3>
            <p className="method-info">{t("contactSupport.helpCenter.info")}</p>
            <p className="method-description">
              {t("contactSupport.helpCenter.description")}
            </p>
            <button className="contact-method-btn" disabled>
              {t("common.comingSoon")}
            </button>
          </div>
        </div>

        {/* Support Hours and Quick Ticket */}
        <div className="support-info-section">
          <div className="info-card">
            <h3>
              <FontAwesomeIcon icon={faClock} className="info-icon" />
              {t("contactSupport.hours.title")}
            </h3>
            <ul className="info-list">
              <li>
                <strong>{t("contactSupport.hours.monFri")}</strong>{" "}
                {t("contactSupport.hours.monFriTime")}
              </li>
              <li>
                <strong>{t("contactSupport.hours.saturday")}</strong>{" "}
                {t("contactSupport.hours.saturdayTime")}
              </li>
              <li>
                <strong>{t("contactSupport.hours.sunday")}</strong>{" "}
                {t("contactSupport.hours.sundayClosed")}
              </li>
              <li className="info-note">{t("contactSupport.hours.note")}</li>
            </ul>
          </div>

          <div className="info-card ticket-card" ref={supportTicketRef}>
            <h3>
              <FontAwesomeIcon icon={faPaperPlane} className="info-icon" />
              {t("contactSupport.ticket.title")}
            </h3>

            {formStatus.message && (
              <div
                className={`form-status compact ${
                  formStatus.error ? "status-error" : "status-success"
                }`}
              >
                <FontAwesomeIcon
                  icon={formStatus.error ? faExclamationCircle : faCheckCircle}
                />
                <span>{formStatus.message}</span>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="support-ticket-form compact-form"
            >
              <div className="form-row-compact">
                <div className="form-group">
                  <label htmlFor="name">
                    {t("contactSupport.ticket.name")}{" "}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t("contactSupport.ticket.namePlaceholder")}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    {t("contactSupport.ticket.email")}{" "}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t("contactSupport.ticket.emailPlaceholder")}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="priority">
                    {t("contactSupport.ticket.priority")}
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    <option value="low">
                      {t("contactSupport.ticket.priority.low")}
                    </option>
                    <option value="medium">
                      {t("contactSupport.ticket.priority.medium")}
                    </option>
                    <option value="high">
                      {t("contactSupport.ticket.priority.high")}
                    </option>
                    <option value="critical">
                      {t("contactSupport.ticket.priority.critical")}
                    </option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  {t("contactSupport.ticket.message")}{" "}
                  <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t("contactSupport.ticket.messagePlaceholder")}
                  rows="3"
                  required
                ></textarea>
              </div>

              <button type="submit" className="submit-btn compact-submit">
                <FontAwesomeIcon icon={faPaperPlane} />
                {t("contactSupport.ticket.submit")}
              </button>
            </form>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="additional-resources">
          <h3>{t("contactSupport.resources.title")}</h3>
          <div className="resources-grid">
            <div className="resource-item">
              <h4>{t("contactSupport.resources.account.title")}</h4>
              <p>{t("contactSupport.resources.account.desc")}</p>
            </div>
            <div className="resource-item">
              <h4>{t("contactSupport.resources.alerts.title")}</h4>
              <p>{t("contactSupport.resources.alerts.desc")}</p>
            </div>
            <div className="resource-item">
              <h4>{t("contactSupport.resources.troubleshoot.title")}</h4>
              <p>{t("contactSupport.resources.troubleshoot.desc")}</p>
            </div>
            <div className="resource-item">
              <h4>{t("contactSupport.resources.prepare.title")}</h4>
              <p>{t("contactSupport.resources.prepare.desc")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactSupport;
